import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { element, string } from 'prop-types';
import { cloneDeep, isEmpty, isEqual, mapValues, omit, partition, union, without } from 'lodash';
import clsx from 'clsx';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useTheme } from '@material-ui/core/styles';
import { Card, CardHeader, CardContent, LinearProgress, TextField } from '@material-ui/core';
import { ErrorBoundary } from 'react-error-boundary';

import PerformanceLog from '../../performance-log/PerformanceLog';
import ErrorMessage from '../../error-message/ErrorMessage';
import Actions from '../actions/Actions';
import MaximizeAction from '../actions/MaximizeAction';
import RemoveAction from '../actions/RemoveAction';
import {
    useFilterState,
    mergeFilterStates,
    pickFilterStates,
    hasFilterStates,
    transformParameters,
    FilterStateContainer,
} from '../../../stores/FilterStateStore';
import FilterRegistry, { createFilterComponent } from '../filter/FilterRegistry';
import Filter, { SUMMARY_LENGTHS } from '../filter/Filter';
import FilterMenu from '../filter-container/FilterMenu';
import { timeRangeFilterId } from '../../filters';
import PanelRegistry from './PanelRegistry';
import useStyles, { calcPanelHeight } from './PanelStyles';
import useActionStyles from '../actions/ActionStyles';
import useIsPrinting from '../../../utilities/PrintUtilities';
import useSize from '../../../utilities/UseSize';
import useDebounced from '../../../utilities/UseDebounced';
import { useAutoHeight, useMaximized } from '../panel-container/Layout';
import { useDashboardContext } from '../dashboard/DashboardContext';
import { useDataService } from '../../../services/ApiDataService';

const PanelContext = createContext();

export function usePanelContext() {
    return useContext(PanelContext);
}

export function useId() {
    const context = usePanelContext();
    return context.id;
}

export function useIsLoading() {
    const context = usePanelContext();
    return [context.loading, context.onLoadingChange];
}

export function useIsAutoHeight() {
    const context = usePanelContext();
    return context.autoHeight;
}

export function useOptions() {
    const context = usePanelContext();
    return [context.options, context.onOptionsChange];
}

export function usePageFilterState() {
    const context = usePanelContext();
    return [context.pageFilterState, context.pageFilterStateActions];
}

export function useOverridesFilterState() {
    const context = usePanelContext();
    return [context.overridesFilterState, context.overridesFilterStateActions];
}

export function useActionsPortal() {
    const context = usePanelContext();
    return context?.actionsPortal;
}

export function useEditingActionsPortal() {
    const context = usePanelContext();
    return context?.editingActionsPortal;
}

function Title({ id, panel, onChange, setActionsPortalRef, setEditingActionsPortalRef }) {
    const { title, hideTitle, hideActions, overrides } = panel.props;
    const classes = useStyles();
    const actionClasses = useActionStyles();
    const isPrinting = useIsPrinting();
    const [maximized, onMaximized] = useMaximized(id);
    const { editing } = useDashboardContext();
    const showActions = !hideActions && !isPrinting;
    const onTitleChange = useDebounced(onChange, 250);
    const titleElement = editing ? (
        <>
            <span
                className={clsx(classes.dragHandle, 'panel-drag-handle')}
                title={i18n._(t`Click and drag to move this panel to a new location`)}
            >
                <span />
            </span>
            <TextField
                fullWidth
                label={i18n._(t`Title`)}
                placeholder={title}
                defaultValue={overrides?.title || title}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => onTitleChange(event.target.value)}
            />
        </>
    ) : (
        <span className={classes.panelTitle} title={overrides?.title || title}>
            {overrides?.title || title}
        </span>
    );
    const actionsElement = editing ? (
        <div>
            <div className={clsx(classes.panelActionPortal, actionClasses.actions)} ref={setEditingActionsPortalRef} />
            <Actions className={classes.panelActions}>
                <RemoveAction id={id} />
            </Actions>
        </div>
    ) : (
        <>
            {showActions && (
                <div>
                    <div className={classes.panelActionPortal} ref={setActionsPortalRef} />
                    {panel.props.expandable && (
                        <Actions className={classes.panelActions}>
                            <MaximizeAction
                                maximized={maximized}
                                onChange={(isMaximized) => onMaximized(id, isMaximized)}
                            />
                        </Actions>
                    )}
                </div>
            )}
        </>
    );

    return !hideTitle && <CardHeader title={titleElement} action={actionsElement} />;
}

function LoadProgress({ loading }) {
    const classes = useStyles();
    if (loading) return <LinearProgress />;
    return <div className={classes.progressPlaceholder} />;
}

function StateSummary({ options, panelFilters, onFilterOverrideSet, onFilterOverrideClear }) {
    const panelId = useId();
    const [panelFilterState] = useFilterState();
    const [overridesFilterState] = useOverridesFilterState();
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const { editing } = useDashboardContext();
    const shouldShow = editing || options?.show === true || isPrinting;
    const [summaryFilters, setSummaryFilters] = useState([]);
    const [partitionedFilters, setPartitionedFilters] = useState(undefined);

    const partitionFilters = useRef();
    partitionFilters.current = () => {
        const { encoded: encodedState = {} } = panelFilterState || {};
        const [activeFilters, optionalFilters] = partition(summaryFilters, (filter) => {
            return encodedState && encodedState[filter.props.id] !== undefined;
        });

        return {
            activeFilters,
            optionalFilters,
        };
    };

    function addFilter(filter) {
        setPartitionedFilters((currentPartitionedFilters) => ({
            ...currentPartitionedFilters,
            activeFilters: [...currentPartitionedFilters.activeFilters, filter],
            optionalFilters: without(currentPartitionedFilters.optionalFilters, filter),
        }));
    }

    function setOverride(filter, newState) {
        const filterStateService = FilterRegistry.getFilterStateService(filter.props.id);
        onFilterOverrideSet(filter.props.id, filterStateService.encode(newState));
    }

    function clearOverride(filter) {
        const hasOverridesState = !!overridesFilterState?.current[filter.props.id];

        // Only clear the override if there is one.
        if (hasOverridesState) {
            onFilterOverrideClear(filter.props.id);
        } else {
            // If there isn't an override, just move the filter from active to optional.
            // This happens when the filter dialog was canceled before applying an
            // override value.
            setPartitionedFilters((currentPartitionedFilters) => ({
                ...currentPartitionedFilters,
                activeFilters: without(currentPartitionedFilters.activeFilters, filter),
                optionalFilters: [...currentPartitionedFilters.optionalFilters, filter],
            }));
        }
    }

    useEffect(() => {
        // Don't bother to create filters if the summary isn't shown.
        if (!shouldShow) return;

        const filterIds = without(
            union(panelFilters.required, panelFilters.optional),
            ...(options?.excludedFilters || [])
        );

        setSummaryFilters(
            filterIds.map((filterId) => {
                try {
                    const filterType = FilterRegistry.getFilterType(filterId);
                    const { options: filterOptions = {} } = filterType.defaultProps || {};
                    return createFilterComponent(filterId, {
                        options:
                            filterId === timeRangeFilterId ? { ...filterOptions, enableCustom: false } : filterOptions,
                        autoRefresh: false,
                    });
                } catch (error) {
                    return (
                        <ErrorMessage key={filterId} id={filterId} error={error} componentStack={error.stack || ''} />
                    );
                }
            })
        );
    }, [options, panelFilters, shouldShow]);

    useEffect(() => {
        setPartitionedFilters(partitionFilters.current());
    }, [panelFilterState]);

    if (!shouldShow) return null;

    return (
        <div className={classes.panelStateSummary}>
            {editing && (
                <FilterMenu
                    optionalFilters={partitionedFilters?.optionalFilters || []}
                    onOptionalFilterSelected={addFilter}
                />
            )}
            {!!panelFilterState &&
                partitionedFilters?.activeFilters?.map((filter) => {
                    const hasState = !!panelFilterState.current[filter.props.id];
                    const hasOverridesState = !!overridesFilterState?.current[filter.props.id];
                    return (
                        <PerformanceLog key={filter.props.id} label={`${filter.type.name}:${filter.props.id}`}>
                            <Filter
                                required={
                                    // All filters are required when not editing, since values can only be
                                    // changed when editing.  When editing, only filters with an override
                                    // can be cleared, so they are not required.
                                    !editing || (editing && hasState && !hasOverridesState)
                                }
                                autoRefresh={
                                    // Only filters with an override should allow auto-refresh.
                                    hasOverridesState
                                }
                                enabled={editing}
                                onChange={(newState) => setOverride(filter, newState)}
                                onRemove={() => clearOverride(filter)}
                                classes={classes}
                                summaryLength={
                                    filter.props.id === timeRangeFilterId && isPrinting
                                        ? SUMMARY_LENGTHS.DEFAULT
                                        : SUMMARY_LENGTHS.SHORT
                                }
                            >
                                {filter}
                            </Filter>
                        </PerformanceLog>
                    );
                })}
            <div className={classes.panelStateSummaryPortal} id={`${panelId}-summary-portal`} />
        </div>
    );
}

function PanelComponentWrapper({
    id,
    panel,
    actionsPortal,
    editingActionsPortal,
    loading,
    autoHeight,
    pageFilterState,
    pageFilterStateActions,
    onContentHeightChange,
    onLoadingChange,
    optionsOverrides,
    onOptionsOverridesChange,
    onFilterOverrideSet,
    onFilterOverrideClear,
}) {
    const classes = useStyles();
    const {
        type: { name: typeName },
        props: { stateSummaryOptions },
    } = panel;
    const panelFilters = useMemo(() => {
        return PanelRegistry.getFiltersForPanelType(typeName);
    }, [typeName]);
    const [overridesFilterState, overridesFilterStateActions] = useFilterState();
    const onOptionsChange = useDebounced(onOptionsOverridesChange, 250);
    const panelContextValue = useMemo(
        () => ({
            id,
            autoHeight,
            loading,
            onLoadingChange,
            options: optionsOverrides,
            onOptionsChange,
            pageFilterState,
            pageFilterStateActions,
            overridesFilterState,
            overridesFilterStateActions,
            actionsPortal,
            editingActionsPortal,
        }),
        [
            id,
            loading,
            onLoadingChange,
            optionsOverrides,
            onOptionsChange,
            autoHeight,
            pageFilterState,
            pageFilterStateActions,
            overridesFilterState,
            overridesFilterStateActions,
            actionsPortal,
            editingActionsPortal,
        ]
    );
    const [panelFilterState, setPanelFilterState] = useState(undefined);
    const resizeRef = useRef();
    const { height: contentHeight } = useSize(resizeRef);

    // This effect combines the page filter state with the panel's filter overrides to produce the
    // panel's overall filter state.
    useEffect(() => {
        if (!pageFilterState || !overridesFilterState) return;

        // Merge the page filter state with the panel's filter overrides.
        const mergedFilterState = mergeFilterStates(overridesFilterState, pageFilterState);

        // Pick only the filter states that apply to this panel from the merged filter states.
        const pickedFilterState = pickFilterStates(
            mergedFilterState,
            union(panelFilters.required, panelFilters.optional)
        );

        // Be sure we have all required filters.
        if (hasFilterStates(pickedFilterState, panelFilters.required)) {
            const newPanelFilterState = transformParameters(pickedFilterState, panelFilters.transformations);

            setPanelFilterState((currentPanelFilterState) => ({
                ...newPanelFilterState,
                // Preserve the previous panel filter state.
                previous: cloneDeep(currentPanelFilterState?.current || {}),
            }));
        }
    }, [pageFilterState, overridesFilterState, panelFilters]);

    // Set content height on each render.  This is necessary to ensure it doesn't get lost after editing.
    if (autoHeight) onContentHeightChange(contentHeight);

    return (
        <PerformanceLog label={panel.type.name}>
            <ErrorBoundary FallbackComponent={ErrorMessage}>
                <PanelContext.Provider value={panelContextValue}>
                    {/* This container contains the panel's combined filter state */}
                    <FilterStateContainer
                        name={`${id}-merged-filter-state`}
                        dataService={useDataService()}
                        manageUrl={false}
                        state={panelFilterState}
                    >
                        <CardContent className="flex-container">
                            <StateSummary
                                options={stateSummaryOptions}
                                panelFilters={panelFilters}
                                onFilterOverrideSet={onFilterOverrideSet}
                                onFilterOverrideClear={onFilterOverrideClear}
                            />
                            <div
                                className={clsx({ 'flex-container': !autoHeight }, classes.panelComponentContainer)}
                                ref={resizeRef}
                            >
                                {panel}
                            </div>
                        </CardContent>
                    </FilterStateContainer>
                </PanelContext.Provider>
            </ErrorBoundary>
        </PerformanceLog>
    );
}

export default function Panel(props) {
    const theme = useTheme();
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const { id, children: panel } = props;
    const { dashboardId, stateSummaryOptions, overrides, hideTitle } = panel.props;
    const showSummary = stateSummaryOptions?.show || isPrinting;
    const [optionsOverrides, setOptionsOverrides] = useState(overrides?.options || {});
    const [filterOverrides, setFilterOverrides] = useState(overrides?.filters || {});
    const [encodedFilterOverrides, setEncodedFilterOverrides] = useState(undefined);
    const [pageFilterState, pageFilterStateActions] = useFilterState();
    const [isLoading, setIsLoading] = useState(false);
    const [autoHeight, onHeightChange] = useAutoHeight(id);
    const { editing, dashboardActions } = useDashboardContext();
    const onHeightChangeDebounced = useDebounced(onHeightChange, 0);
    const [actionsPortal, setActionsPortal] = useState();
    const [editingActionsPortal, setEditingActionsPortal] = useState();

    const handleContentHeightChange = useCallback(
        (contentHeight) => {
            if (contentHeight !== 0) {
                // When printing, using the debounced function creates a race condition where the PDF gets
                // generated before the new height is applied to the panel.  For this reason, we don't
                // want to debounce height changes when printing.  However, we need to debounce otherwise
                // in order to avoid the following React warning:
                //
                // "Warning: Cannot update a component (`Layout`) while rendering a different component (`PanelComponentWrapper`)."
                //
                const onHeightChangeFunc = isPrinting ? onHeightChange : onHeightChangeDebounced;
                onHeightChangeFunc(id, calcPanelHeight(theme, !hideTitle, showSummary, contentHeight));
            }
        },
        [id, theme, hideTitle, showSummary, onHeightChangeDebounced, onHeightChange, isPrinting]
    );

    const handleTitleChange = useCallback(
        (newTitle) => {
            dashboardActions.setPanelOverrides(dashboardId, (currentPanelOverrides) => {
                return isEmpty(newTitle)
                    ? omit(currentPanelOverrides, 'title')
                    : {
                          ...currentPanelOverrides,
                          title: newTitle,
                      };
            });
        },
        [dashboardId, dashboardActions]
    );

    const handleFilterOverrideSet = useCallback(
        (filterId, encodedState) => {
            setFilterOverrides((currentFilterOverrides) => {
                const newFilterOverrides = {
                    ...currentFilterOverrides,
                    [filterId]: {
                        encodedState,
                    },
                };

                // Update the panel overrides if we're editing a dashboard and the
                // filter overrides actually changed.
                if (editing && !isEqual(currentFilterOverrides, newFilterOverrides)) {
                    dashboardActions.setPanelOverrides(dashboardId, (currentPanelOverrides) => {
                        return isEmpty(newFilterOverrides)
                            ? omit(currentPanelOverrides, 'filters')
                            : {
                                  ...currentPanelOverrides,
                                  filters: newFilterOverrides,
                              };
                    });
                }

                return newFilterOverrides;
            });
        },
        [editing, dashboardId, dashboardActions]
    );

    const handleFilterOverrideClear = useCallback(
        (filterId) => {
            setFilterOverrides((currentFilterOverrides) => {
                const newFilterOverrides = omit(currentFilterOverrides, filterId);

                // Update the panel overrides if we're editing a dashboard and the
                // filter overrides actually changed.
                if (editing && !isEqual(currentFilterOverrides, newFilterOverrides)) {
                    dashboardActions.setPanelOverrides(dashboardId, (currentPanelOverrides) => {
                        return isEmpty(newFilterOverrides)
                            ? omit(currentPanelOverrides, 'filters')
                            : {
                                  ...currentPanelOverrides,
                                  filters: newFilterOverrides,
                              };
                    });
                }

                return newFilterOverrides;
            });
        },
        [editing, dashboardId, dashboardActions]
    );

    const handleOptionsOverridesChange = useCallback(
        (newOptionsOverrides) => {
            // Ignore options changes if we're not editing.
            if (!editing) return;

            setOptionsOverrides((currentOptionsOverrides) => {
                // Don't update anything if the options didn't really change.
                if (isEqual(currentOptionsOverrides, newOptionsOverrides)) return currentOptionsOverrides;

                dashboardActions.setPanelOverrides(dashboardId, (currentPanelOverrides) => {
                    return isEmpty(newOptionsOverrides)
                        ? omit(currentPanelOverrides, 'options')
                        : {
                              ...currentPanelOverrides,
                              options: newOptionsOverrides,
                          };
                });

                return newOptionsOverrides;
            });
        },
        [editing, dashboardId, dashboardActions]
    );

    function setActionsPortalRef(el) {
        setActionsPortal(el);
    }

    function setEditingActionsPortalRef(el) {
        setEditingActionsPortal(el);
    }

    useEffect(() => {
        // If not editing, we need to reset the filter overrides and options overrides to their original values so that
        // edits are discarded when editing is canceled.
        if (editing) return;

        setFilterOverrides((currentFilterOverrides) => {
            return isEqual(currentFilterOverrides, overrides?.filters) ? currentFilterOverrides : overrides?.filters;
        });
        setOptionsOverrides((currentOptionsOverrides) => {
            return isEqual(currentOptionsOverrides, overrides?.options) ? currentOptionsOverrides : overrides?.options;
        });
    }, [overrides, editing]);

    useEffect(() => {
        // The filter overrides changed, so we need to update the encoded filter overrides, which are used to initialize
        // the overrides filter state store.  This will keep the store's state updated as overrides are changed by the
        // user.
        setEncodedFilterOverrides(filterOverrides ? mapValues(filterOverrides, 'encodedState') : {});
    }, [filterOverrides]);

    return (
        <div
            id={id}
            className={clsx({
                'flex-container': true,
                [classes.panel]: true,
                [classes.hiddenTitle]: hideTitle,
                [classes.visibleSummary]: showSummary,
            })}
        >
            <Card
                className={clsx(classes.panelCard, {
                    'flex-container': !autoHeight || !isPrinting,
                    [classes.draggablePanel]: editing,
                })}
            >
                <Title
                    id={id}
                    panel={panel}
                    onChange={handleTitleChange}
                    setActionsPortalRef={setActionsPortalRef}
                    setEditingActionsPortalRef={setEditingActionsPortalRef}
                />
                <LoadProgress loading={isLoading} />
                {/* This container contains the panel's filter overrides */}
                <FilterStateContainer
                    name={`${id}-overrides-filter-state`}
                    dataService={useDataService()}
                    manageUrl={false}
                    encodedState={encodedFilterOverrides}
                >
                    <PanelComponentWrapper
                        id={id}
                        panel={panel}
                        actionsPortal={actionsPortal}
                        editingActionsPortal={editingActionsPortal}
                        pageFilterState={pageFilterState}
                        pageFilterStateActions={pageFilterStateActions}
                        loading={isLoading}
                        onLoadingChange={setIsLoading}
                        autoHeight={autoHeight}
                        onContentHeightChange={handleContentHeightChange}
                        optionsOverrides={optionsOverrides}
                        onOptionsOverridesChange={handleOptionsOverridesChange}
                        onFilterOverrideSet={handleFilterOverrideSet}
                        onFilterOverrideClear={handleFilterOverrideClear}
                    />
                </FilterStateContainer>
            </Card>
        </div>
    );
}

Panel.propTypes = {
    id: string.isRequired,
    children: element.isRequired,
};
